import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { ArrowRight, Lightning } from '@phosphor-icons/react';
import { useSelectionStore } from '@/state/selectionStore';
import { useEpisodeStore } from '@/state/episodeStore';
import { useGenerationStore } from '@/state/generationStore';
import { useGenerateImage } from '@/hooks/useGenerateImage';
import { useGenerateVideo } from '@/hooks/useGenerateVideo';
import { useBatchGenerate } from '@/hooks/useBatchGenerate';
import { useDraft } from '@/hooks/useDraft';
import { ReferenceChip } from './ReferenceChip';
import { toast } from '@/state/toastStore';

interface Props {
  episodeUuid: string;
  showAssetToggle?: boolean;     // true on the scene detail page
}

export function BottomInput({ episodeUuid, showAssetToggle = false }: Props) {
  const inputMode = useSelectionStore((s) => s.inputMode);
  const setInputMode = useSelectionStore((s) => s.setInputMode);
  const assetMode = useSelectionStore((s) => s.assetMode);
  const setAssetMode = useSelectionStore((s) => s.setAssetMode);
  const freshPrompt = useSelectionStore((s) => s.freshPrompt);
  const setFreshPrompt = useSelectionStore((s) => s.setFreshPrompt);
  const rawPrompt = useSelectionStore((s) => s.rawPrompt);
  const setRawPrompt = useSelectionStore((s) => s.setRawPrompt);
  const refs = useSelectionStore((s) => s.pendingReferences);
  const removeRefScene = useSelectionStore((s) => s.removeReferenceScene);
  const removeRefCast = useSelectionStore((s) => s.removeReferenceCast);
  const activeSceneId = useSelectionStore((s) => s.activeSceneId);
  const activeScene = useEpisodeStore((s) =>
    activeSceneId ? s.scenes.find((sc) => sc.id === activeSceneId) ?? null : null,
  );

  const generateImage = useGenerateImage();
  const generateVideo = useGenerateVideo();
  const batchGenerate = useBatchGenerate();
  const inFlightCount = useGenerationStore((s) => s.inFlightSceneIds.size);
  // Batch and video kickoff are short, but we still gate them so the user
  // doesn't accidentally double-fire those specific paths.
  const [busyOther, setBusyOther] = useState(false);
  const [extractCast, setExtractCast] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Draft autosave keyed by active scene
  useDraft(episodeUuid, 'scene', activeSceneId, rawPrompt, setRawPrompt);

  // Keep textarea height in sync with content (1–6 rows)
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 168)}px`;
  }, [rawPrompt]);

  const placeholder = computePlaceholder(inputMode, assetMode, freshPrompt);
  const isVideo = showAssetToggle && assetMode === 'video';
  // Only block the button for batch/video flows. Single-image gen is
  // fire-and-forget so the user can queue up many in parallel.
  const canSubmit = rawPrompt.trim().length > 0 && !busyOther;

  async function submit() {
    if (!canSubmit) return;
    setError(null);

    // Snapshot the current inputs so the user can keep typing the next prompt
    // while this generation runs in the background.
    const promptText = rawPrompt;
    const promptRefs = refs;
    const promptFresh = freshPrompt;

    if (inputMode === 'batch') {
      setBusyOther(true);
      try {
        await batchGenerate(episodeUuid, { text: promptText, extractCast });
        setRawPrompt('');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Generation failed.');
      } finally {
        setBusyOther(false);
      }
      return;
    }

    if (isVideo) {
      if (!activeScene) {
        setError('Pick a scene to animate first.');
        return;
      }
      const heroImageId =
        activeScene.heroImageId ?? activeScene.imageIds.at(-1) ?? null;
      if (!heroImageId) {
        setError('No source image — generate an image first.');
        return;
      }
      setBusyOther(true);
      try {
        await generateVideo(episodeUuid, {
          sceneId: activeScene.id,
          sourceImageId: heroImageId,
          prompt: promptText,
          references: promptRefs,
          fresh: promptFresh,
          duration: 6,
          model: 'veo-fast',
        });
        setRawPrompt('');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Generation failed.');
      } finally {
        setBusyOther(false);
      }
      return;
    }

    // Single image gen — fire-and-forget. Clear the input immediately so the
    // user can stack more generations while this one is in flight. Errors
    // surface as toasts because the input state has already moved on.
    setRawPrompt('');
    void generateImage(episodeUuid, {
      prompt: promptText,
      references: promptRefs,
      fresh: promptFresh,
    }).catch((err) => {
      toast.err(err instanceof Error ? err.message : 'Generation failed.');
    });
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void submit();
    }
  }

  return (
    <div className="border-t border-line-1 bg-surface-1 px-5 py-4 shadow-elev-1">
      <div className="mx-auto flex max-w-4xl flex-col gap-3">
        {/* Top row: pills + chips */}
        <div className="flex flex-wrap items-center gap-3">
          {showAssetToggle && (
            <SegmentedPill
              options={[
                { id: 'image', label: 'Image' },
                { id: 'video', label: 'Video' },
              ]}
              value={assetMode}
              onChange={(v) => setAssetMode(v as 'image' | 'video')}
            />
          )}
          <SegmentedPill
            options={[
              { id: 'single', label: 'Single' },
              { id: 'batch', label: 'Batch' },
            ]}
            value={inputMode}
            onChange={(v) => setInputMode(v as 'single' | 'batch')}
          />
          {freshPrompt && (
            <span className="inline-flex items-center gap-1 rounded-sm bg-accent-soft px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-accent">
              <Lightning size={10} weight="fill" /> Raw
            </span>
          )}
          {(refs.sceneIds.length > 0 || refs.castIds.length > 0) && (
            <div className="flex flex-1 flex-wrap items-center gap-1.5">
              <span className="text-[11px] uppercase tracking-[0.14em] text-text-3">
                refs:
              </span>
              {refs.sceneIds.map((id) => (
                <ReferenceChip
                  key={`s-${id}`}
                  kind="scene"
                  sceneId={id}
                  episodeUuid={episodeUuid}
                  onRemove={() => removeRefScene(id)}
                />
              ))}
              {refs.castIds.map((id) => (
                <ReferenceChip
                  key={`c-${id}`}
                  kind="cast"
                  castId={id}
                  episodeUuid={episodeUuid}
                  onRemove={() => removeRefCast(id)}
                />
              ))}
            </div>
          )}
          {activeScene && (
            <span className="ml-auto rounded-sm bg-surface-3 px-2 py-1 font-mono text-[10px] tabular-nums text-text-2">
              → Scene {String(activeScene.order).padStart(2, '0')}
            </span>
          )}
        </div>

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          rows={1}
          value={rawPrompt}
          onChange={(e) => setRawPrompt(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className="w-full resize-none rounded-md bg-surface-inset px-3 py-2 text-[14px] text-text-1 placeholder:text-text-4 outline-none ring-1 ring-line-2 transition-shadow focus:ring-line-3"
        />

        {/* Bottom row */}
        <div className="flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.14em] text-text-3">
          <span className="text-text-4">
            template + style controlled in Style tab →
          </span>
          {isVideo && (
            <>
              <span className="text-text-4">duration: 6s ▾</span>
              <span className="text-text-4">model: fast ▾</span>
            </>
          )}
          {inputMode === 'batch' && (
            <ExtractCastCheckbox checked={extractCast} onChange={setExtractCast} />
          )}
          <FreshToggle on={freshPrompt} onChange={setFreshPrompt} />
          {inFlightCount > 0 && (
            <span className="ml-auto inline-flex items-center gap-1 rounded-sm bg-accent-soft px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-accent">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent" />
              {inFlightCount} in flight
            </span>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className={clsx(
              'inline-flex h-9 items-center gap-2 rounded-md px-4 font-medium uppercase tracking-wider transition-all',
              inFlightCount === 0 && 'ml-auto',
              canSubmit
                ? 'bg-accent text-text-on-accent hover:bg-accent-hover'
                : 'cursor-not-allowed bg-surface-3 text-text-4',
            )}
          >
            {busyOther ? 'Generating…' : 'Generate'}
            <ArrowRight size={14} weight="bold" />
          </button>
        </div>

        {error && (
          <p className="text-[12px] text-err" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

function SegmentedPill({
  options,
  value,
  onChange,
}: {
  options: { id: string; label: string }[];
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <div className="inline-flex rounded-md bg-surface-2 p-0.5">
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onChange(opt.id)}
          className={clsx(
            'rounded-sm px-3 py-1 text-[12px] font-medium transition-colors',
            value === opt.id
              ? 'bg-accent-soft text-accent'
              : 'text-text-3 hover:text-text-2',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function ExtractCastCheckbox({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 normal-case tracking-normal text-text-3 hover:text-text-2">
      <span
        className={clsx(
          'flex h-3.5 w-3.5 items-center justify-center rounded-sm border-2 transition-colors',
          checked
            ? 'border-accent bg-accent text-text-on-accent'
            : 'border-text-4 bg-transparent',
        )}
      >
        {checked && <Lightning size={10} weight="bold" />}
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      Extract cast first
    </label>
  );
}

function FreshToggle({
  on,
  onChange,
}: {
  on: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 normal-case tracking-normal">
      <span
        className={clsx(
          'relative inline-flex h-4 w-7 items-center rounded-full transition-colors',
          on ? 'bg-accent' : 'bg-surface-3',
        )}
      >
        <span
          className={clsx(
            'absolute h-3 w-3 rounded-full bg-text-on-accent transition-transform',
            on ? 'translate-x-3.5' : 'translate-x-0.5',
          )}
        />
      </span>
      <input
        type="checkbox"
        checked={on}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      <span className={on ? 'text-text-1' : 'text-text-3'}>
        <Lightning size={11} className="-mt-px mr-1 inline" weight={on ? 'fill' : 'regular'} />
        Fresh prompt
      </span>
    </label>
  );
}

function computePlaceholder(
  inputMode: string,
  assetMode: string,
  freshPrompt: boolean,
): string {
  if (freshPrompt) return 'Type the literal prompt — no template, no style prefix.';
  if (inputMode === 'batch') return 'Describe the story, song, or scene list…';
  if (assetMode === 'video') return 'Describe the motion…';
  return 'What do you want to create?';
}
